import {
  SUBSCRIPTION_CONTRACT_META_KEY,
  SubscriptionProduct,
} from "@cappelendamm/e-commerce-services";
import {
  SelectedGiftSection,
  SubscriptionCheckoutPart,
} from "@/checkout/Sections/SubscriptionSection/types";
import { type NormalizedProductVariant } from "@cappelendamm/catalogue/crystallize/product-translator.js";

const getBookAuthors = (
  product: NormalizedProductVariant,
): string | undefined => {
  if (product.itemType !== "book") {
    return undefined;
  }

  const authors = product.itemDetails.authors
    .map((author) => author?.name)
    .filter((author): author is string => Boolean(author));

  return authors.length > 0 ? authors.join(", ") : undefined;
};

export function selectGiftOptionFromProduct(
  product: NormalizedProductVariant,
  selectedSkus: string[],
) {
  const description =
    "description" in product && typeof product.description === "string"
      ? product.description
      : undefined;

  return {
    name: product.name ?? product.sku,
    description,
    isBook: product.itemType === "book",
    author: getBookAuthors(product),
    price: typeof product.price === "number" ? product.price : null,
    images: product.images,
    sku: product.sku,
    selected: selectedSkus.some((sku) => product.sku === sku),
  };
}

export const getSelectedGiftSkus = (
  gifts: NormalizedProductVariant[],
  selectCount: number,
  selectedGiftSkus: string[],
) => {
  let selectedGifts = selectedGiftSkus;

  if (selectCount === gifts.length) {
    selectedGifts = gifts.map((gift) => gift.sku);
  }

  return gifts.map((gift) => selectGiftOptionFromProduct(gift, selectedGifts));
};

const getGiftSectionValues = (
  section: SelectedGiftSection,
  selectedGiftSkus: string[],
) => ({
  count: section.count,
  options: getSelectedGiftSkus(section.gifts, section.count, selectedGiftSkus),
});

export const getDefaultSubscriptionValues = (
  subscription: SubscriptionProduct,
  gifts: NormalizedProductVariant[],
  giftSections: SelectedGiftSection[],
  initialBookSku: string | undefined,
  selectedGiftSkus: string[],
): SubscriptionCheckoutPart => {
  const hasGiftSections = giftSections && giftSections.length > 0;
  return {
    subscription: {
      sku: subscription.sku,
      planIdentifier: subscription.plan.identifier,
      name: subscription.name,
    },
    gifts: !hasGiftSections
      ? getSelectedGiftSkus(gifts, subscription.gifts.count, selectedGiftSkus)
      : [],
    giftSections: hasGiftSections
      ? giftSections.map((section) =>
          getGiftSectionValues(section, selectedGiftSkus),
        )
      : [],
    meta: {
      [SUBSCRIPTION_CONTRACT_META_KEY.INITIAL_SKU]: initialBookSku || "",
      [SUBSCRIPTION_CONTRACT_META_KEY.CAMPAIGN_BOOKS]:
        subscription.campaignBooks.map(({ sku }) => sku).join(","),
      [SUBSCRIPTION_CONTRACT_META_KEY.CHILD_NAME]: "",
      [SUBSCRIPTION_CONTRACT_META_KEY.CHILD_BIRTH_DATE]: {
        day: "",
        month: "",
        year: "",
      },
    },
  };
};
